import type { Season } from '../engine/seasons/SeasonTypes';
import type { BackgroundMusicId } from '../engine/audio/AudioDirector';
import { normalizeWorldEvent, type RuntimeWorldEvent } from '../overlay/WorldEventEffects';

export interface RuntimeConfig {
  readonly kingdomId: string;
  readonly showDebug: boolean;
  readonly showAllLabels: boolean;
  readonly visualPolish: boolean;
  readonly waterMode: 'tiles';
  readonly growthLevel: number;
  readonly season?: Season;
  readonly worldTime: RuntimeWorldTime;
  readonly characters: readonly RuntimeCharacter[];
  readonly buildings: readonly RuntimeBuilding[];
  readonly worldEvent: RuntimeWorldEvent | null;
  readonly activityEvents: readonly RuntimeActivityEvent[];
  readonly backgroundMusic: BackgroundMusicId;
  readonly branding: RuntimeBranding;
  readonly resources: RuntimeKingdomResources;
	readonly cameraZoomStep: number;
}

export interface RuntimeBranding {
  readonly primaryColor: string;
  readonly secondaryColor: string;
  readonly logoUrl: string;
  readonly showLogoOnBanners: boolean;
}

export interface RuntimeKingdomResources {
  readonly wood: number;
  readonly stone: number;
  readonly food: number;
  readonly gold: number;
  readonly iron: number;
  readonly clay: number;
  readonly tools: number;
  readonly weapons: number;
  readonly armor: number;
  readonly siegeEngines: number;
  readonly bricks: number;
  readonly buildingMaterials: number;
  readonly prestige: number;
  readonly crownCrystals: number;
  readonly defense: number;
  readonly satisfaction: number;
}

export interface RuntimeWorldTime {
  readonly originUnixMs: number;
  readonly serverUnixMs: number;
  readonly seasonLengthMs: number;
}

export interface RuntimeCharacter {
  readonly name: string;
  readonly profession: string;
  readonly hairColor: string;
  readonly shirtColor: string;
  readonly pantsColor: string;
  readonly gender: 'male' | 'female';
	readonly isStreamer: boolean;
  readonly x: number;
  readonly y: number;
  readonly task: string;
  readonly targetX: number;
  readonly targetY: number;
  readonly taskStartedAt: string;
}

export interface RuntimeBuilding {
  readonly kind: string;
  readonly label: string;
  readonly level: number;
  readonly x: number;
  readonly y: number;
}

export interface RuntimeActivityEvent {
  readonly type: string;
  readonly message: string;
  readonly createdAt: number;
}

declare global {
  interface Window {
    StreamKingdomsConfig?: Partial<RuntimeConfig>;
    StreamKingdomsUpdateCharacters?: (characters: unknown) => void;
    StreamKingdomsUpdateBuildings?: (buildings: unknown) => void;
    StreamKingdomsUpdateActivityEvents?: (events: unknown) => void;
    StreamKingdomsUpdateWorldEvent?: (event: unknown) => void;
    StreamKingdomsUpdateAudio?: (backgroundMusic: unknown) => void;
    StreamKingdomsUpdateResources?: (resources: unknown) => void;
	StreamKingdomsUpdateCamera?: (camera: unknown) => void;
    StreamKingdomsCameraState?: () => {
      readonly zoom: number;
      readonly targetZoom: number;
      readonly growthZoom: number;
    };
    StreamKingdomsCastleState?: () => {
      readonly level: number;
      readonly visible: boolean;
      readonly x: number;
      readonly y: number;
      readonly width: number;
      readonly height: number;
    } | null;
	StreamKingdomsDebugState?: () => {
		readonly visualPolish: boolean;
		readonly waterMode: 'tiles';
		readonly resources: RuntimeKingdomResources;
		readonly season: {
			readonly year: number;
			readonly currentSeason: Season;
			readonly seasonDay: number;
		};
		readonly units: readonly { readonly name: string; readonly x: number; readonly y: number; readonly activity: string; readonly hidden: boolean }[];
		readonly buildings: readonly { readonly id: string; readonly kind: string; readonly x: number; readonly y: number }[];
		readonly decorations: readonly { readonly id: string; readonly kind: string; readonly x: number; readonly y: number }[];
	};
  }
}

export function readRuntimeConfig(params = new URLSearchParams(window.location.search)): RuntimeConfig {
  const polishParameter = params.get('visualPolish') ?? params.get('polish');
  const visualPolish =
    window.StreamKingdomsConfig?.visualPolish !== false &&
    polishParameter !== '0' &&
    polishParameter !== 'false';

  const requestedSeason = window.StreamKingdomsConfig?.season ?? params.get('season') ?? undefined;
  const season = isSeason(requestedSeason) ? requestedSeason : undefined;
  const kingdomId =
    String(params.get('kingdom') || params.get('seed') || window.StreamKingdomsConfig?.kingdomId || 'oldgamingman77-kingdom').trim() ||
    'oldgamingman77-kingdom';
  const requestedGrowth = Number(
    params.get('growthLevel') ||
    params.get('level') ||
    window.StreamKingdomsConfig?.growthLevel ||
    readOverlayGrowthLevel() ||
    1,
  );
  const previewEvent = params.get('worldEvent');

  return {
    kingdomId,
    visualPolish,
    waterMode: 'tiles',
    growthLevel: Math.max(1, Math.min(6, Number.isFinite(requestedGrowth) ? Math.floor(requestedGrowth) : 1)),
    characters: normalizeRuntimeCharacters(
      window.StreamKingdomsConfig?.characters?.length ? window.StreamKingdomsConfig.characters : readOverlayCharacters(),
    ),
    buildings: normalizeRuntimeBuildings(
      window.StreamKingdomsConfig?.buildings?.length ? window.StreamKingdomsConfig.buildings : readOverlayBuildings(),
    ),
    worldEvent: normalizeWorldEvent(
      previewEvent
        ? { event_type: previewEvent, title: params.get('eventTitle') || previewEvent, command: params.get('eventCommand') || '' }
        : window.StreamKingdomsConfig?.worldEvent ?? readOverlayWorldEvent(),
    ),
    activityEvents: normalizeRuntimeActivityEvents(
      window.StreamKingdomsConfig?.activityEvents?.length
        ? window.StreamKingdomsConfig.activityEvents
        : readOverlayActivityEvents(),
    ),
    backgroundMusic: normalizeBackgroundMusic(
      params.get('backgroundMusic') ??
      window.StreamKingdomsConfig?.backgroundMusic ??
      readOverlayBackgroundMusic(),
    ),
    branding: normalizeRuntimeBranding(window.StreamKingdomsConfig?.branding ?? readOverlayBranding()),
    resources: normalizeRuntimeResources(window.StreamKingdomsConfig?.resources ?? readOverlayResources()),
	cameraZoomStep: normalizeCameraZoomStep(window.StreamKingdomsConfig?.cameraZoomStep ?? readOverlayCamera()),
    worldTime: normalizeWorldTime(window.StreamKingdomsConfig?.worldTime ?? readOverlayWorldTime()),
    season,
    showAllLabels:
      window.StreamKingdomsConfig?.showAllLabels === true ||
      params.get('labels') === '1' ||
      params.get('showAllLabels') === '1',
    showDebug:
      !visualPolish &&
      (window.StreamKingdomsConfig?.showDebug === true || params.get('debug') === '1' || params.get('showDebug') === '1'),
  };
}

export function normalizeCameraZoomStep(value: unknown): number {
	const row = value && typeof value === 'object' ? value as Record<string, unknown> : {};
	const parsed = Number(row.manual_step ?? row.manualStep ?? value ?? 0);
	return Math.max(-3, Math.min(3, Number.isFinite(parsed) ? Math.round(parsed) : 0));
}

function readOverlayCamera(): unknown {
	const raw = document.body?.getAttribute('data-overlay-initial');
	if (!raw) return undefined;
	try {
		return (JSON.parse(raw) as { camera?: Record<string, unknown> }).camera;
	} catch {
		return undefined;
	}
}

export function normalizeRuntimeResources(value: unknown): RuntimeKingdomResources {
  const resources = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const amount = (camelCase: string, snakeCase = camelCase): number => {
    const parsed = Number(resources[camelCase] ?? resources[snakeCase] ?? 0);
    return Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 0;
  };
  return {
    wood: amount('wood'),
    stone: amount('stone'),
    food: amount('food'),
    gold: amount('gold'),
    iron: amount('iron'),
    clay: amount('clay'),
    tools: amount('tools'),
    weapons: amount('weapons'),
    armor: amount('armor'),
    siegeEngines: amount('siegeEngines', 'siege_engines'),
    bricks: amount('bricks'),
    buildingMaterials: amount('buildingMaterials', 'building_materials'),
    prestige: amount('prestige'),
    crownCrystals: amount('crownCrystals', 'crown_crystals'),
    defense: amount('defense'),
    satisfaction: amount('satisfaction'),
  };
}

function readOverlayResources(): unknown {
  const raw = document.body?.getAttribute('data-overlay-initial');
  if (!raw) return undefined;
  try {
    return (JSON.parse(raw) as { kingdom?: Record<string, unknown> }).kingdom;
  } catch {
    return undefined;
  }
}

export function normalizeRuntimeBranding(value: unknown): RuntimeBranding {
  const branding = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const logoCandidate = String(branding.logoUrl ?? branding.logo_url ?? '').trim();
  let logoUrl = '';
  if (logoCandidate) {
    try {
      const parsed = new URL(logoCandidate, window.location.href);
      if (parsed.protocol === 'https:' || parsed.protocol === 'http:') logoUrl = parsed.href;
    } catch {
      logoUrl = '';
    }
  }
  return {
    primaryColor: normalizeHexColor(branding.primaryColor ?? branding.primary_color, '#d6a84f'),
    secondaryColor: normalizeHexColor(branding.secondaryColor ?? branding.secondary_color, '#4f8fd6'),
    logoUrl,
    showLogoOnBanners:
      branding.showLogoOnBanners === true ||
      branding.show_logo_on_banners === true ||
      branding.show_logo_on_banners === 1 ||
      branding.show_logo_on_banners === '1',
  };
}

function readOverlayBranding(): unknown {
  const raw = document.body?.getAttribute('data-overlay-initial');
  if (!raw) return undefined;
  try {
    const kingdom = (JSON.parse(raw) as { kingdom?: Record<string, unknown> }).kingdom ?? {};
    return {
      primary_color: kingdom.primary_color,
      secondary_color: kingdom.secondary_color,
      logo_url: kingdom.logo_url,
      show_logo_on_banners: kingdom.show_logo_on_banners,
    };
  } catch {
    return undefined;
  }
}

function normalizeHexColor(value: unknown, fallback: string): string {
  const color = String(value ?? '').trim().toLowerCase();
  return /^#[0-9a-f]{6}$/.test(color) ? color : fallback;
}

export function normalizeBackgroundMusic(value: unknown): BackgroundMusicId {
  const selection = String(value ?? '').trim().toLowerCase();
  return selection === 'off' || selection === 'taverns-secret' || selection === 'whispers-old-road'
    ? selection
    : 'whispers-old-road';
}

function readOverlayBackgroundMusic(): unknown {
  const raw = document.body?.getAttribute('data-overlay-initial');
  if (!raw) return undefined;
  try {
    return (JSON.parse(raw) as { audio?: { background_music?: unknown } }).audio?.background_music;
  } catch {
    return undefined;
  }
}

function readOverlayGrowthLevel(): number | undefined {
  const raw = document.body?.getAttribute('data-overlay-initial');
  if (!raw) return undefined;
  try {
    const stage = Number((JSON.parse(raw) as { growth?: { stage?: unknown } }).growth?.stage);
    return Number.isFinite(stage) ? stage : undefined;
  } catch {
    return undefined;
  }
}

function readOverlayWorldTime(): unknown {
  const raw = document.body?.getAttribute('data-overlay-initial');
  if (!raw) return null;
  try {
    return (JSON.parse(raw) as { world_time?: unknown }).world_time ?? null;
  } catch {
    return null;
  }
}

function normalizeWorldTime(value: unknown): RuntimeWorldTime {
  const row = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const serverUnixMs = Number(row.server_unix_ms ?? row.serverUnixMs);
  const originUnixMs = Number(row.origin_unix_ms ?? row.originUnixMs);
  const seasonLengthMs = Number(row.season_length_ms ?? row.seasonLengthMs);
  const now = Date.now();
  const defaultYearLength = 28 * 24 * 60 * 60 * 1000;
  return {
    serverUnixMs: Number.isFinite(serverUnixMs) && serverUnixMs > 0 ? serverUnixMs : now,
    originUnixMs: Number.isFinite(originUnixMs) && originUnixMs > 0
      ? originUnixMs
      : Math.floor(now / defaultYearLength) * defaultYearLength,
    seasonLengthMs: Number.isFinite(seasonLengthMs) && seasonLengthMs >= 60_000
      ? seasonLengthMs
      : 7 * 24 * 60 * 60 * 1000,
  };
}

function readOverlayWorldEvent(): unknown {
  const raw = document.body?.getAttribute('data-overlay-initial');
  if (!raw) return null;
  try {
    return (JSON.parse(raw) as { world_event?: unknown }).world_event ?? null;
  } catch {
    return null;
  }
}

function readOverlayCharacters(): unknown {
  const raw = document.body?.getAttribute('data-overlay-initial');
  if (!raw) return [];
  try {
    const data = JSON.parse(raw) as { city?: readonly Record<string, unknown>[] };
    return (data.city || [])
      .filter((row) => row.type === 'citizen')
      .map((row) => ({
        name: row.display_label || row.label || '',
        profession: row.profession || 'bauer',
        hairColor: row.hair_color || '#5a351f',
        shirtColor: row.shirt_color || '#a84432',
        pantsColor: row.pants_color || '#46301f',
		gender: row.character_gender === 'female' ? 'female' : 'male',
        isStreamer: row.is_streamer === true || row.is_streamer === 1,
        x: row.x,
        y: row.y,
        task: row.action || 'idle',
        targetX: row.target_x,
        targetY: row.target_y,
        taskStartedAt: row.task_started_at || '',
      }));
  } catch {
    return [];
  }
}

function readOverlayBuildings(): unknown {
  const raw = document.body?.getAttribute('data-overlay-initial');
  if (!raw) return [];
  try {
    const data = JSON.parse(raw) as { city?: readonly Record<string, unknown>[] };
    return (data.city || []).filter((row) => isRuntimeBuildingKind(String(row.type || '')));
  } catch {
    return [];
  }
}

function readOverlayActivityEvents(): unknown {
  const raw = document.body?.getAttribute('data-overlay-initial');
  if (!raw) return [];
  try {
    return (JSON.parse(raw) as { player_activity?: unknown }).player_activity ?? [];
  } catch {
    return [];
  }
}

export function normalizeRuntimeCharacters(value: unknown): readonly RuntimeCharacter[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 40).map((row, index) => {
    const character = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
    return {
      name: String(character.name || `Einwohner ${index + 1}`).slice(0, 120),
      profession: String(character.profession || 'bauer').replace(/[^a-z0-9_-]/gi, '').toLowerCase() || 'bauer',
      hairColor: normalizeColor(character.hairColor, '#5a351f'),
      shirtColor: normalizeColor(character.shirtColor, '#a84432'),
      pantsColor: normalizeColor(character.pantsColor, '#46301f'),
		gender: character.gender === 'female' ? 'female' : 'male',
		isStreamer: character.isStreamer === true,
      x: clampPercent(character.x),
      y: clampPercent(character.y),
      task: String(character.task ?? character.action ?? 'idle').replace(/[^a-z0-9_-]/gi, '').toLowerCase() || 'idle',
      targetX: clampPercent(character.targetX ?? character.target_x),
      targetY: clampPercent(character.targetY ?? character.target_y),
      taskStartedAt: String(character.taskStartedAt ?? character.task_started_at ?? '').slice(0, 40),
    };
  });
}

export function normalizeRuntimeBuildings(value: unknown): readonly RuntimeBuilding[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 80).flatMap((row) => {
    const building = row && typeof row === 'object' ? row as Record<string, unknown> : {};
    const rawKind = String(building.kind ?? building.type ?? '').replace(/[^a-z0-9_-]/gi, '').toLowerCase();
    if (!isRuntimeBuildingKind(rawKind)) return [];
    const kind = rawKind === 'woodcamp' ? 'woodcutter_camp' : rawKind;
    return [{
      kind,
      label: String(building.label || kind).slice(0, 120),
      level: Math.max(1, Math.min(20, Math.floor(Number(building.level) || 1))),
      x: clampPercent(building.x),
      y: clampPercent(building.y),
    }];
  });
}

export function normalizeRuntimeActivityEvents(value: unknown): readonly RuntimeActivityEvent[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 8).flatMap((row, index) => {
    const event = row && typeof row === 'object' ? row as Record<string, unknown> : {};
    const message = String(event.message || '').trim().slice(0, 180);
    if (!message) return [];
    const parsedTime = Date.parse(String(event.createdAt ?? event.created_at ?? ''));
    return [{
      type: String(event.type || 'chat-command').replace(/[^a-z0-9_-]/gi, '').toLowerCase() || 'chat-command',
      message,
      createdAt: Number.isFinite(parsedTime) ? parsedTime : Date.now() - index,
    }];
  });
}

function isRuntimeBuildingKind(kind: string): boolean {
  return [
    'house', 'farm', 'mine', 'woodcamp', 'quarry', 'iron_mine', 'clay_pit',
    'smelter', 'smithy', 'mill', 'bakery', 'stable', 'watchtower', 'spy_office', 'brickyard',
    'builder_yard', 'warehouse', 'wall', 'siege_workshop', 'barracks', 'market',
    'castle', 'mage_tower',
  ].includes(kind);
}

function normalizeColor(value: unknown, fallback: string): string {
  const color = String(value || '').toLowerCase();
  return /^#[0-9a-f]{6}$/.test(color) ? color : fallback;
}

function clampPercent(value: unknown): number {
  const number = Number(value);
  return Math.max(0, Math.min(100, Number.isFinite(number) ? number : 50));
}

function isSeason(value: unknown): value is Season {
  return value === 'spring' || value === 'summer' || value === 'autumn' || value === 'winter';
}
